/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-restricted-syntax */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDButton from "components/Basics/MDButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SourceActions from "redux-react/actions/sourceActions";

const MoveFileDialog = ({ formattedFolders, onClose, selectedAssets, reloadAssets, folderKey }) => {
	const dispatch = useDispatch();
	const [folders, setFolders] = useState(formattedFolders);
	const [selectedItem, setSelectedItem] = useState(null);
	const { t } = useTranslation();

	const handleConfirm = () => {
		if (selectedItem) {
			const ids = selectedAssets.map(asset => asset.id);

			dispatch(
				SourceActions.moveAssets(ids, selectedItem.id, () => {
					reloadAssets();
					onClose?.();
				})
			);
		}
	};

	const deselectAllItems = items => {
		return items.map(item => {
			const updatedItem = { ...item, isSelected: false };
			if (item.children && item.children.length > 0) {
				updatedItem.children = deselectAllItems(item.children);
			}
			return updatedItem;
		});
	};

	const selectItemById = (items, id) => {
		return items.map(item => {
			if (item.id === id) {
				return { ...item, isSelected: true };
			}
			if (item.children && item.children.length > 0) {
				return { ...item, children: selectItemById(item.children, id) };
			}
			return { ...item, isSelected: false };
		});
	};

	const toggleFolderOpen = id => {
		const updateFolders = items =>
			items.map(item => {
				if (item.id === id) {
					return { ...item, isOpen: !item.isOpen };
				}
				if (item.children && item.children.length > 0) {
					return { ...item, children: updateFolders(item.children) };
				}
				return item;
			});
		setFolders(updateFolders(folders));
	};

	const selectFolder = id => {
		const deselectedFolders = deselectAllItems(folders);
		const updatedFolders = selectItemById(deselectedFolders, id);
		setFolders(updatedFolders);

		const findSelectedItem = items => {
			for (let item of items) {
				if (item.id === id) {
					return item;
				}
				if (item.children && item.children.length > 0) {
					const found = findSelectedItem(item.children);
					if (found) return found;
				}
			}
			return null;
		};
		setSelectedItem(findSelectedItem(updatedFolders));
	};

	const FolderItem = ({ folder, depth = 0 }) => {
		// Ne pas modifier directement les propriétés du dossier
		const isCurrentlySelected = folder.isSelected;
		const hasChildren = folder.children && folder.children.length > 0;

		return (
			<>
				<ListItem
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						pl: 2 + depth * 2,
						backgroundColor: isCurrentlySelected ? "#e0e0e0" : "inherit",
						"&:hover": { backgroundColor: "#f1f1f1" }
					}}
				>
					<Radio
						checked={isCurrentlySelected}
						onChange={() => selectFolder(folder.id)}
						sx={{
							color: "#666",
							"&.Mui-checked": { color: "#1976d2" }
						}}
					/>
					<ListItemText
						primary={
							<Typography sx={{ fontWeight: "300", fontSize: "0.875rem" }}>
								{folder.name}
							</Typography>
						}
						onClick={() => {
							if (hasChildren) {
								toggleFolderOpen(folder.id);
							} else {
								selectFolder(folder.id);
							}
						}}
					/>
					{hasChildren && (
						<Box sx={{ ml: "auto" }} onClick={() => toggleFolderOpen(folder.id)}>
							{folder.isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</Box>
					)}
				</ListItem>
				{hasChildren && (
					<Collapse in={folder.isOpen}>
						{folder.children.map(child => (
							<FolderItem key={child.id} folder={child} depth={depth + 1} />
						))}
					</Collapse>
				)}
			</>
		);
	};

	return (
		<Dialog
			open
			onClose={onClose}
			aria-labelledby="asset-selection-dialog-title"
			maxWidth="xs"
			fullWidth
		>
			<DialogTitle
				id="asset-selection-dialog-title"
				sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
			>
				{t("SOURCES.FOLDER.selectFolder")}
				<IconButton aria-label="close" onClick={onClose} sx={{ color: "#666" }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Box sx={{ width: "100%", fontFamily: "Arial, sans-serif" }}>
					<List
						sx={{
							width: "100%",
							maxHeight: 300,
							overflowY: "auto",
							backgroundColor: "#fff"
						}}
					>
						{folders
							?.filter(it => it?.key === folderKey)
							.map(folder => (
								<FolderItem key={folder.id} folder={folder} />
							))}
					</List>
				</Box>
			</DialogContent>

			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={onClose}>
					{t("SETTINGS.cancel")}
				</MDButton>
				<MDButton variant="contained" color="info" onClick={handleConfirm}>
					{t("SETTINGS.move")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default MoveFileDialog;
